import { internalProductCostOn, rebateRateFor } from './productMaster'
import type { PddDailySkuOrderRecord } from './pinduoduo'
import type { CompanyProductOverrideRecord, InternalProductInfoRecord, StoreCostProfile, StoreCostVersion } from './types'

export interface PinduoduoProfitEstimate {
  profit: number
  rebateAmount: number
  profitWithRebate: number
  productCostAmount: number
  packagingCost: number
  giftCost: number
  shippingInsuranceCost: number
  shippingCost: number
  platformFee: number
  taxFee: number
  fulfillmentCost: number
  rateFee: number
  coveredRevenue: number
  coveredItems: number
  totalRevenue: number
  totalItems: number
  coveredRows: number
  totalRows: number
  complete: boolean
  missingCostSkuCodes: string[]
  missingStoreCostDates: string[]
  stableUnitValues: {
    productCost: number | null
    packagingCost: number | null
    giftCost: number | null
    shippingInsuranceCost: number | null
    shippingCost: number | null
    rebateAmount: number | null
    platformFeeRate: number | null
    taxRate: number | null
  }
}

export const pinduoduoBreakEvenRoi = (estimate: PinduoduoProfitEstimate | null | undefined) =>
  estimate?.complete && estimate.coveredRevenue > 0 && estimate.profitWithRebate > 0
    ? estimate.coveredRevenue / estimate.profitWithRebate
    : null

const latestReadyVersion = (versions: StoreCostVersion[] | undefined, date: string) => [...(versions || [])]
  .filter((version) => version.calculationReady && version.effectiveDate <= date)
  .sort((left, right) => right.effectiveDate.localeCompare(left.effectiveDate))[0]

export const pinduoduoStoreCostOn = (profile: StoreCostProfile | undefined, date: string) =>
  latestReadyVersion(profile?.actualSettlementVersions, date)
  || latestReadyVersion(profile?.defaultVersions, date)
  || latestReadyVersion(profile?.platformTemplateVersions, date)

const onlyValue = (values: Set<number>) => values.size === 1 ? [...values][0] : null

const isAccessory = (
  records: InternalProductInfoRecord[],
  override: CompanyProductOverrideRecord | undefined,
) => override?.isAccessory ?? records.some((record) =>
  record.productName.includes('配件')
  || (record.ecommercePrice !== null && record.ecommercePrice > 0 && record.ecommercePrice < 30),
)

export function pinduoduoCostParametersOn(
  profile: StoreCostProfile | undefined,
  records: InternalProductInfoRecord[],
  override: CompanyProductOverrideRecord | undefined,
  date: string,
) {
  const storeCost = pinduoduoStoreCostOn(profile, date)
  const productCost = internalProductCostOn(records, override, date)
  const accessory = isAccessory(records, override)
  const giftExcluded = accessory || productCost.isBrandSpecial
  return {
    storeCost,
    productCost,
    accessory,
    giftExcluded,
    giftCost: giftExcluded ? 0 : (storeCost?.giftCost ?? 0),
    rebateRate: rebateRateFor(accessory, productCost.isBrandSpecial, productCost.productCost),
  }
}

export function estimatePinduoduoProfit(
  rows: PddDailySkuOrderRecord[],
  profile: StoreCostProfile | undefined,
  companyRecordsByCode: Map<string, InternalProductInfoRecord[]>,
  companyOverridesByCode: Map<string, CompanyProductOverrideRecord>,
): PinduoduoProfitEstimate {
  let profit = 0
  let rebateAmount = 0
  let productCostAmount = 0
  let packagingCost = 0
  let giftCostAmount = 0
  let shippingInsuranceCost = 0
  let shippingCost = 0
  let platformFee = 0
  let taxFee = 0
  let fulfillmentCost = 0
  let rateFee = 0
  let coveredRevenue = 0
  let coveredItems = 0
  let coveredRows = 0
  const missingCostSkuCodes = new Set<string>()
  const missingStoreCostDates = new Set<string>()
  const unitValues = {
    productCost: new Set<number>(),
    packagingCost: new Set<number>(),
    giftCost: new Set<number>(),
    shippingInsuranceCost: new Set<number>(),
    shippingCost: new Set<number>(),
    rebateAmount: new Set<number>(),
    platformFeeRate: new Set<number>(),
    taxRate: new Set<number>(),
  }

  for (const row of rows) {
    const records = companyRecordsByCode.get(row.skuCode) || []
    const override = companyOverridesByCode.get(row.skuCode)
    const parameters = pinduoduoCostParametersOn(profile, records, override, row.date)
    const { storeCost, productCost } = parameters
    if (!storeCost) {
      missingStoreCostDates.add(row.date)
      continue
    }
    if (productCost.productCost === null) {
      missingCostSkuCodes.add(row.skuCode)
      continue
    }
    const rowProductCost = productCost.productCost * row.soldItemCount
    const rowPackagingCost = (storeCost.packagingCost ?? 0) * row.soldItemCount
    const rowGiftCost = parameters.giftCost * row.soldItemCount
    const rowShippingInsuranceCost = (storeCost.shippingInsuranceFee ?? 0) * row.soldItemCount
    const rowShippingCost = (storeCost.shippingFee ?? 0) * row.soldItemCount
    const rowFulfillmentCost = rowProductCost + rowPackagingCost + rowGiftCost + rowShippingInsuranceCost + rowShippingCost
    const rowPlatformFee = row.merchantReceivable * (storeCost.platformFeeRate ?? 0) / 100
    const rowTaxFee = row.merchantReceivable * (storeCost.taxRate ?? 0) / 100
    const rowRateFee = rowPlatformFee + rowTaxFee
    const unitRebate = parameters.rebateRate === null ? 0 : productCost.productCost * parameters.rebateRate
    const rowRebate = unitRebate * row.soldItemCount
    unitValues.productCost.add(productCost.productCost)
    unitValues.packagingCost.add(storeCost.packagingCost ?? 0)
    unitValues.giftCost.add(parameters.giftCost)
    unitValues.shippingInsuranceCost.add(storeCost.shippingInsuranceFee ?? 0)
    unitValues.shippingCost.add(storeCost.shippingFee ?? 0)
    unitValues.rebateAmount.add(unitRebate)
    unitValues.platformFeeRate.add(storeCost.platformFeeRate ?? 0)
    unitValues.taxRate.add(storeCost.taxRate ?? 0)
    productCostAmount += rowProductCost
    packagingCost += rowPackagingCost
    giftCostAmount += rowGiftCost
    shippingInsuranceCost += rowShippingInsuranceCost
    shippingCost += rowShippingCost
    platformFee += rowPlatformFee
    taxFee += rowTaxFee
    fulfillmentCost += rowFulfillmentCost
    rateFee += rowRateFee
    rebateAmount += rowRebate
    profit += row.merchantReceivable - rowFulfillmentCost - rowRateFee
    coveredRevenue += row.merchantReceivable
    coveredItems += row.soldItemCount
    coveredRows += 1
  }

  return {
    profit,
    rebateAmount,
    profitWithRebate: profit + rebateAmount,
    productCostAmount,
    packagingCost,
    giftCost: giftCostAmount,
    shippingInsuranceCost,
    shippingCost,
    platformFee,
    taxFee,
    fulfillmentCost,
    rateFee,
    coveredRevenue,
    coveredItems,
    totalRevenue: rows.reduce((sum, row) => sum + row.merchantReceivable, 0),
    totalItems: rows.reduce((sum, row) => sum + row.soldItemCount, 0),
    coveredRows,
    totalRows: rows.length,
    complete: coveredRows === rows.length && rows.length > 0,
    missingCostSkuCodes: [...missingCostSkuCodes].sort(),
    missingStoreCostDates: [...missingStoreCostDates].sort(),
    stableUnitValues: {
      productCost: onlyValue(unitValues.productCost),
      packagingCost: onlyValue(unitValues.packagingCost),
      giftCost: onlyValue(unitValues.giftCost),
      shippingInsuranceCost: onlyValue(unitValues.shippingInsuranceCost),
      shippingCost: onlyValue(unitValues.shippingCost),
      rebateAmount: onlyValue(unitValues.rebateAmount),
      platformFeeRate: onlyValue(unitValues.platformFeeRate),
      taxRate: onlyValue(unitValues.taxRate),
    },
  }
}
